import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Lottie from "lottie-react";
import octopusAnimation from "../assets/Octopus.json"; // Assuming the same octopus animation is available

const NotFound: React.FC = () => {
  useEffect(() => {
    // Particle animation for background
    const container = document.getElementById("particles");
    if (container) {
      const particleCount = 50;
      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement("div");
        particle.className = "particle";
        const size = Math.random() * 4 + 2;
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        const posX = Math.random() * 100;
        const posY = Math.random() * 100;
        particle.style.left = `${posX}%`;
        particle.style.top = `${posY}%`;
        container.appendChild(particle);

        setInterval(() => {
          const newPosX = parseFloat(particle.style.left) + (Math.random() * 2 - 1) * 0.1;
          const newPosY = parseFloat(particle.style.top) + (Math.random() * 2 - 1) * 0.1;
          particle.style.left = `${newPosX}%`;
          particle.style.top = `${newPosY}%`;
        }, 1000);
      }
    }
  }, []);

  return (
    <div className="relative min-h-screen">
      <style>{`
        :root {
          --cyber-green: #a8fd2d;
          --electric-cyan: #22c1c3;
          --cosmic-pink: #ff1493;
          --midnight-black: #000000;
          --glass-bg: rgba(10, 14, 43, 0.3);
          --card-blur: 12px;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          font-family: 'Inter', sans-serif;
        }

        body {
          background-color: var(--midnight-black);
          color: white;
          margin: 0;
          overflow-x: hidden;
        }

        .particles-container {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: -1;
          overflow: hidden;
        }

        .particle {
          position: absolute;
          background: linear-gradient(135deg, var(--electric-cyan), var(--cyber-green));
          border-radius: 50%;
          opacity: 0.6;
          filter: blur(2px);
        }

        .container {
          max-width: 448px;
          margin: 0 auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          text-align: center;
          gap: 16px;
        }

        .agent-card {
          position: relative;
          background: transparent;
          border-radius: 15px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          padding: 24px;
          transition: all 0.3s ease;
          overflow: hidden;
          word-wrap: break-word;
          width: 100%;
          max-width: 400px;
          will-change: transform;
        }

        .agent-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: var(--glass-bg);
          backdrop-filter: blur(var(--card-blur));
          z-index: -1;
          border-radius: 15px;
        }

        .agent-card:hover {
          box-shadow: 0 0 15px var(--cyber-green);
          border-color: var(--cyber-green);
        }

        .agent-card:active {
          box-shadow: 0 0 25px var(--cyber-green);
          border-color: var(--cyber-green);
        }

        .agent-card * {
          position: relative;
          z-index: 1;
        }

        h1 {
          font-size: 2.5rem;
          font-weight: bold;
          background: linear-gradient(45deg, var(--electric-cyan), var(--cyber-green));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 8px;
        }

        p {
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 16px;
        }

        .link-button {
          display: inline-block;
          padding: 8px 16px;
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          background: var(--glass-bg);
          backdrop-filter: blur(var(--card-blur));
          color: var(--cyber-green);
          font-size: 14px;
          font-weight: 500;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .link-button:hover {
          border-color: var(--cyber-green);
          color: white;
          background: linear-gradient(45deg, var(--electric-cyan), var(--cyber-green));
          transform: scale(1.05);
        }

        .link-button:active {
          transform: scale(0.95);
        }

        ul {
          list-style: none;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .lottie-container {
          width: 150px;
          height: 150px;
          margin-bottom: 16px;
        }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }

        @media (max-width: 768px) {
          .container {
            padding: 12px;
          }

          .agent-card {
            padding: 16px;
          }

          h1 {
            font-size: 2rem;
          }

          p {
            font-size: 0.9rem;
          }

          .link-button {
            padding: 6px 12px;
            font-size: 12px;
          }

          .lottie-container {
            width: 120px;
            height: 120px;
          }
        }
      `}</style>

      <div className="particles-container" id="particles"></div>
      <div className="container">
        <div className="agent-card animate-fade-in">
          <div className="lottie-container">
            <Lottie animationData={octopusAnimation} loop={true} />
          </div>
          <h1>404 - Page Not Found</h1>
          <p>Oops! Looks like drOcto got lost in cyberspace! 🚀</p>
          <p>
            Head to the <Link to="/" className="link-button">Home Page</Link> or
          </p>
          <ul>

            <li>
              <Link to="/schools" className="link-button">Register a School</Link>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default NotFound;